import React from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Box,
  Typography,
  Popover,
  Divider,
  Button,
  Grid,
  ListItemButton,
  ListItemText,
  Grow,
} from '@mui/material';
import {
  Apps as AppsIcon,
  Close as CloseIcon,
  AccountCircle as AccountCircleIcon,
  Public as PublicIcon,
  Map as MapIcon,
  YouTube as YouTubeIcon,
  Shop as ShopIcon,
  CalendarMonth as CalendarIcon,
  Translate as TranslateIcon,
  DriveFolderUpload as DriveIcon,
  MailOutline as MailIcon,
  PhotoCamera as PhotoIcon,
  Article as ArticleIcon,
  VideoCall as MeetIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {useOrganization} from "../../shared/OrganizationContext";
import { useTour } from '../../shared/TourContext';
import PassiveSearchView from "../PassiveSearch/PassiveSearchView";
import useAuth from '../../hooks/useAuth';

const Header = () => {
  const { user, signOut } = useAuth();
  const { organization, setOrganization } = useOrganization();
  const { setRunTour } = useTour(); 
  
  const [accountAnchor, setAccountAnchor] = React.useState(null);
  const [appsAnchor, setAppsAnchor]       = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null); 
  
  const navigate = useNavigate();
  
  if (!user) {
    return null;
  }

  const handleLogout = () => {
    signOut();
    setAccountAnchor(null);
  };
  
  const switchOrg = org => {
    setOrganization(org);
    setAccountAnchor(null);
    navigate('/');
  };

  /** Account pop-over **/
  const AccountPopover = (
    <Popover
      open={Boolean(accountAnchor)}
      anchorEl={accountAnchor}
      onClose={() => setAccountAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      TransitionComponent={Grow}
      PaperProps={{
        sx: {
          bgcolor: '#eef0fa', 
          borderRadius: 4,    
          p: 0,               
          width: 360,         
          boxShadow: '0 5px 15px rgba(0,0,0,0.1)', 
        },
      }}
    >
      {/* Email + Close Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2, pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} color="text.primary">{user.email}</Typography>
        <IconButton size="small" aria-label="Close account menu" onClick={() => setAccountAnchor(null)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />

      {/* Avatar + Greeting */}
      <Box display="flex" flexDirection="column" alignItems="center" my={2.5}>
        <Avatar sx={{ width: 72, height: 72, bgcolor: '#5e8c3f', fontSize: '2rem', mb: 1 }}>
          {user.email[0].toUpperCase()}
        </Avatar>
        <Typography variant="h5" color="text.primary">Hi, {user.firstName ?? 'there'}!</Typography>
      </Box>

      {/* Top action buttons */}
      <Box px={2} mb={1}>
        <Button fullWidth variant="outlined" sx={{ borderRadius: '20px', borderColor: 'grey.400', color: 'text.primary', textTransform: 'none', py: 1 }}>
          Edit Account
        </Button>
      </Box>
      <Box px={2} mb={1}>
        <Button fullWidth variant="outlined" sx={{ borderRadius: '20px', borderColor: 'grey.400', color: 'text.primary', textTransform: 'none', py: 1 }}>
          Organization Settings
        </Button>
      </Box>
      <Box px={2} mb={2}>
        <Button
          fullWidth
          variant="outlined"
          sx={{ borderRadius: '20px', borderColor: 'grey.400', color: 'text.primary', textTransform: 'none', py: 1 }}
          component={Link} 
          to="/passive-search" 
          onClick={() => {
            setAccountAnchor(null); 
          }}
        >
          Passive Search
        </Button>
      </Box>

      <Divider />

      {/* Organization switcher */}
      <Typography variant="caption" display="block" sx={{ px: 2.5, pt: 1.5, color: 'text.secondary', fontWeight: 500 }}>SWITCH ORGANIZATION</Typography>
      <Box sx={{ bgcolor: 'transparent', borderRadius: 3, maxHeight: 180, overflowY: 'auto', my: 1, mx: 1.5 }}>
        {user?.roles?.map(({ organization: org }) => (
          <ListItemButton
            key={org.uuid}
            selected={org.uuid === organization?.uuid} 
            onClick={() => switchOrg(org)}
            sx={{ borderRadius: 2, mb: 0.5, '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.selected' }} }}
          >
            <ListItemText primary={org.descriptiveName} primaryTypographyProps={{ fontWeight: 500, color: org.uuid === organization?.uuid ? 'primary.main' : 'text.primary' }} />
          </ListItemButton>
        ))}
      </Box>

      <Divider />
      {/* Sign out */}
      <Box px={2} py={1.5}>
        <Button fullWidth variant="outlined" sx={{ borderRadius: '20px', borderColor: 'grey.400', color: 'text.primary', textTransform: 'none', py: 1 }} onClick={handleLogout}>
          Sign Out
        </Button>
      </Box>
    </Popover>
  );

  /** Apps launcher pop-over **/
  const launcherItems = [
    { icon: <AccountCircleIcon sx={{ color: '#5e8c3f' }} />, label: 'Account', url: null },
    { icon: <PublicIcon sx={{ color: '#4285f4' }} />, label: 'Search', url: 'https://www.google.com/' },
    { icon: <MapIcon sx={{ color: '#34a853' }} />, label: 'Maps', url: 'https://maps.google.com/' },
    { icon: <YouTubeIcon sx={{ color: '#ff0000' }} />, label: 'YouTube', url: 'https://www.youtube.com/' },
    { icon: <ShopIcon sx={{ color: '#ea4335' }} />, label: 'Play', url: 'https://play.google.com/' },
    { icon: <CalendarIcon sx={{ color: '#4285f4' }} />, label: 'Calendar', url: 'https://calendar.google.com/' },
    { icon: <TranslateIcon sx={{ color: '#4285f4' }} />, label: 'Translate', url: 'https://translate.google.com/' },
    { icon: <DriveIcon sx={{ color: '#f4b400' }} />, label: 'Drive', url: 'https://drive.google.com/' },
    { icon: <MailIcon sx={{ color: '#ea4335' }} />, label: 'Gmail', url: 'https://mail.google.com/' },
    { icon: <PhotoIcon sx={{ color: '#fbbc05' }} />, label: 'Photos', url: 'https://photos.google.com/' },
    { icon: <ArticleIcon sx={{ color: '#4285f4' }} />, label: 'News', url: 'https://news.google.com/' },
    { icon: <MeetIcon sx={{ color: '#34a853' }} />, label: 'Meet', url: 'https://meet.google.com/' },
  ];

  const AppsPopover = (
    <Popover
      open={Boolean(appsAnchor)}
      anchorEl={appsAnchor}
      onClose={() => setAppsAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      TransitionComponent={Grow}
      PaperProps={{
        sx: {
          bgcolor: '#eef0fa',
          borderRadius: 4,
          p: 2,
          width: 320,
          maxHeight: {xs: '70vh', sm: 480},
          overflowY: 'auto',
          boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Grid container spacing={1}>
        {launcherItems.map(({ icon, label, url }) => (
          <Grid key={label} item xs={4} textAlign="center">
            <IconButton
              aria-label={label}
              onClick={() => {
                if (url) {
                  window.open(url, '_blank'); 
                } else {
                  alert(label + ' URL not configured');
                }
                setAppsAnchor(null); 
              }}
              sx={{
                flexDirection: 'column',
                p: 1.5, 
                borderRadius: 2, 
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              {React.cloneElement(icon, { sx: { ...icon.props.sx, fontSize: 32, mb: 0.5 } })}
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>{label}</Typography>
            </IconButton>
          </Grid>
        ))}
      </Grid>
    </Popover>
  );

  const open = Boolean(anchorEl);

  const handleHelpClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

const handleViewHelp = () => {
    console.log('View Help clicked');
    // could add some general help documentation or link to supportAdd commentMore actions
    handleClose();
  };

  return (
    <>
      <AppBar elevation={0} sx={{ bgcolor: 'common.white', color: 'text.primary', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar sx={{ position: 'relative', minHeight: {xs: 56, sm: 64}, px: {xs: 1, sm: 2} }}>
          <IconButton component={Link} to="/" edge="start" aria-label="Home" sx={{ p: 0, mr: {xs:1, sm:2} }}>
            <img src="/ct_logo_black.png" alt="Candidate Tools Logo" style={{ height: '48px', display: 'block' }} />
          </IconButton>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: {xs: 1, sm:2} }}>
            <IconButton
              color="inherit"
              aria-label="help"
              onClick={handleHelpClick}
              sx={{
                marginLeft: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <HelpIcon />
            </IconButton>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  width: 200,
                  borderRadius: 2,
                  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
                },
              }}
            >
              <ListItemButton onClick={() => {
                  setRunTour(true);
                  setAnchorEl(null);
                }}>
                <ListItemText primary="Take Tour" />
              </ListItemButton>
            </Popover>
            
            <IconButton aria-label="Apps launcher" onClick={e => setAppsAnchor(e.currentTarget)} sx={{color: 'text.secondary'}}>
              <AppsIcon />
            </IconButton>
            <IconButton aria-label="Account settings" onClick={e => setAccountAnchor(e.currentTarget)}>
              <Avatar sx={{ bgcolor: '#5e8c3f', width: {xs: 28, sm: 32}, height: {xs:28, sm:32} }}>
                {user?.email ? user.email[0].toUpperCase() : <AccountCircleIcon />}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {AccountPopover}
      {AppsPopover}
    </>
  );
};

export default Header;
